import Service from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { getOwner } from '@ember/application';
import { task, timeout } from 'ember-concurrency';

function parseVersion(versionString) {
  let [semver, commit] = versionString.split('_');
  let parts = semver.split('.').map(Number);
  return { major: parts[0] ?? 0, minor: parts[1] ?? 0, patch: parts[2] ?? 0, sha: commit };
}

function diffType(current, latest) {
  let c = parseVersion(current);
  let l = parseVersion(latest);

  if (l.major > c.major) return 'major';
  if (l.minor > c.minor) return 'minor';
  if (l.patch > c.patch) return 'patch';
  if (l.sha !== c.sha) return 'sha';
  return null;
}

const DEFAULT_INTERVAL = 60_000;

export default class VersionService extends Service {
  @tracked latestVersion = null;
  @tracked updateType = null;

  constructor() {
    super(...arguments);
    let config = getOwner(this).resolveRegistration('config:environment');
    this.currentVersion = config.APP.version;
    this.versionUrl = config.APP.versionUrl;
    this.interval = config.APP.versionCheckInterval ?? DEFAULT_INTERVAL;
    this.poll.perform();
  }

  get updateAvailable() {
    return this.updateType !== null;
  }

  get isMajorUpdate() {
    return this.updateType === 'major';
  }

  get isMinorUpdate() {
    return this.updateType === 'minor';
  }

  get isPatchUpdate() {
    return this.updateType === 'patch';
  }

  get isShaUpdate() {
    return this.updateType === 'sha';
  }

  get currentSha() {
    return parseVersion(this.currentVersion).sha;
  }

  get latestSha() {
    return this.latestVersion ? parseVersion(this.latestVersion).sha : null;
  }

  reloadAppTask = task(async () => {
    window.location.reload()
  })

  poll = task(async () => {
    while (!this.updateAvailable) {
      await timeout(this.interval);

      try {
        let response = await fetch(this.versionUrl);
        if (!response.ok) continue;
        let text = await response.text();
        let latest = text.trim();
        let type = diffType(this.currentVersion, latest);
        if (type) {
          this.latestVersion = latest;
          this.updateType = type;
        }
      } catch {
        // network blips are silent
      }
    }
  });
}
